import { FunctionComponent, KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faArrowsRotate,
	faEllipsisVertical,
	faPencil,
	faThumbtack,
	faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { classNames } from "@/utils/classNames";

/**
 * The only part of a pinned tab this chip renders. Deliberately narrower than either overview's
 * full tab type so the component is shared verbatim by the Lead List and Campaigns strips, whose
 * `filters` payloads have different shapes (PREC-3880).
 */
export interface PinnedTabChipModel {
	id: number;
	name: string;
}

interface PinnedFilterTabButtonProps {
	tab: PinnedTabChipModel;
	isActive: boolean;
	onSelect: () => void;
	onRename: (name: string) => void;
	onUpdate: () => void;
	onDelete: () => void;
}

/**
 * A single pinned filter tab in an overview tab strip (Lead Lists or Campaigns). Renders the pin
 * icon + name,
 * a kebab menu (rename / update to current filters / delete) revealed on hover or while active,
 * and an inline rename input (double-click the name or pick "Rename tab").
 */
export const PinnedFilterTabButton: FunctionComponent<PinnedFilterTabButtonProps> = ({
	tab,
	isActive,
	onSelect,
	onRename,
	onUpdate,
	onDelete,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.pinTab.${key}`);

	const [isRenaming, setIsRenaming] = useState(false);
	const [draftName, setDraftName] = useState(tab.name);

	const startRename = () => {
		setDraftName(tab.name);
		setIsRenaming(true);
	};

	const commitRename = () => {
		const trimmed = draftName.trim();

		if (trimmed) {
			onRename(trimmed);
		}

		setIsRenaming(false);
	};

	const handleRenameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			commitRename();
		} else if (event.key === "Escape") {
			setIsRenaming(false);
		}
	};

	if (isRenaming) {
		return (
			<div className="flex items-center border-b-2 border-brand-light">
				<input
					autoFocus
					className="my-1 ml-3 mr-2 w-36 rounded-md border border-brand-light px-2 py-1 text-sm font-medium text-text outline-none"
					value={draftName}
					onBlur={commitRename}
					onChange={(event) => setDraftName(event.target.value)}
					onKeyDown={handleRenameKeyDown}
				/>
			</div>
		);
	}

	return (
		<div
			className={classNames(
				"group relative flex items-center border-b-2 transition-colors whitespace-nowrap",
				isActive ? "border-brand-light" : "border-transparent"
			)}
		>
			<button
				className={classNames(
					"flex cursor-pointer items-center gap-1.5 py-2 pl-4 pr-1 text-sm font-medium",
					isActive ? "text-brand-light" : "text-text-meta hover:text-text"
				)}
				type="button"
				onClick={onSelect}
			>
				<Icon className="h-3 w-3" icon={faThumbtack} />
				<span onDoubleClick={startRename}>{tab.name}</span>
			</button>
			<ContextMenu
				data={[
					[
						{
							title: ts("rename"),
							icon: <Icon className="h-3.5 w-3.5" icon={faPencil} />,
							onClick: startRename,
						},
						{
							title: ts("update"),
							icon: <Icon className="h-3.5 w-3.5" icon={faArrowsRotate} />,
							onClick: onUpdate,
						},
						{
							title: ts("delete"),
							icon: <Icon className="h-3.5 w-3.5" icon={faTrashCan} />,
							onClick: onDelete,
						},
					],
				]}
				position="bottom-right"
				width="[180px]"
			>
				<span
					className={classNames(
						"mr-2 flex h-5 w-5 cursor-pointer items-center justify-center rounded text-text-meta transition-opacity hover:bg-gray-100 hover:text-text",
						isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
					)}
				>
					<Icon className="h-3.5 w-3.5" icon={faEllipsisVertical} />
				</span>
			</ContextMenu>
		</div>
	);
};
